import { getBasePath } from 'utils/getBasePath';

// Navigation service for programmatic navigation outside React components

interface ErrorDetails {
    data?: any;
    config?: any;
    [key: string]: any;
}

interface StoredErrorDetails extends ErrorDetails {
    status: number;
    timestamp: string;
    url: string;
}

interface NavigateOptions {
    // Replace the current history entry instead of adding one: for a page the
    // user must not come back to with Back (it would only send them here again).
    replace?: boolean;
}

type NavigatorFunction = (path: string, options?: NavigateOptions) => void;

// Error codes that should trigger error page navigation
type ErrorPageStatus = 502 | 503;

class NavigationService {
    private navigator: NavigatorFunction | null = null;

    setNavigator(navigator: NavigatorFunction): void {
        this.navigator = navigator;
    }

    navigate(path: string, options: NavigateOptions = {}): void {
        if (this.navigator) {
            this.navigator(path, options);
        } else {
            // Fallback to window.location if navigator not set
            console.warn('NavigationService: Navigator not set, falling back to window.location');
            const url = `${getBasePath()}${path}`;
            if (options.replace) {
                window.location.replace(url);
            } else {
                window.location.href = url;
            }
        }
    }

    // Navigate to error page with console log preservation
    navigateToError(errorCode: ErrorPageStatus, errorDetails: ErrorDetails = {}): void {
        // Store error details for debugging
        const fullErrorDetails: StoredErrorDetails = {
            status: errorCode,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            ...errorDetails,
        };

        sessionStorage.setItem('lastError', JSON.stringify(fullErrorDetails));

        // Navigate to error page
        this.navigate(`/error?code=${errorCode}`);
    }
}

// Export types for use in other files
export type { ErrorDetails, StoredErrorDetails, NavigatorFunction, NavigateOptions, ErrorPageStatus };

// Export singleton instance
export const navigationService = new NavigationService();
export default navigationService;
